import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { models as modelsApi } from "../../lib/api";
import type { ModelChoice } from "../../lib/types";
import { Modal } from "../shared/Modal";
import { Button } from "../shared/Button";
import { IconButton } from "../shared/IconButton";
import { Trash } from "../shared/Icon";
import { cn } from "../../lib/cn";

/**
 * Operator-editable model catalog. Lists the DB-backed models the task/project
 * dropdowns offer, lets you add a new one the day a provider ships it, remove
 * one, or "Refresh" to restore the curated built-ins to their current set.
 *
 * Alias rows (opus/sonnet/haiku) resolve to the latest model of that family at
 * the CLI, so they never go stale — that's how the catalog stays current
 * without Maestro ever holding a provider API key.
 */
export function ModelCatalogModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["models"], queryFn: modelsApi.list });
  const list = data?.models ?? [];
  const refreshedAt = data?.refreshed_at
    ? new Date(data.refreshed_at).toLocaleString()
    : null;

  const [modelId, setModelId] = useState("");
  const [label, setLabel] = useState("");
  const [cliBinary, setCliBinary] = useState("claude");
  const [isAlias, setIsAlias] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["models"] });

  const add = useMutation({
    mutationFn: () =>
      modelsApi.add({ model_id: modelId, label, cli_binary: cliBinary, is_alias: isAlias }),
    onSuccess: () => {
      setModelId("");
      setLabel("");
      setIsAlias(false);
      setErr(null);
      invalidate();
    },
    onError: (e) => setErr(e instanceof Error ? e.message : String(e)),
  });
  const remove = useMutation({
    mutationFn: (m: ModelChoice) => modelsApi.remove(m.cli_binary, m.id),
    onSuccess: invalidate,
  });
  const refresh = useMutation({ mutationFn: modelsApi.refresh, onSuccess: invalidate });

  const groups = Array.from(new Set(list.map((m) => m.cli_binary)));

  return (
    <Modal onClose={onClose} title="Models" size="lg">
      <div className="space-y-5">
        <p className="text-small text-text-secondary">
          The models offered in task and project forms. <strong>Alias</strong>{" "}
          rows (opus/sonnet/haiku) always resolve to the latest model of that
          family at dispatch. Add a new model the day it ships — no redeploy.
        </p>

        {groups.map((bin) => (
          <div key={bin} className="space-y-1.5">
            <div className="text-caption text-text-tertiary">{bin}</div>
            <ul className="space-y-1">
              {list
                .filter((m) => m.cli_binary === bin)
                .map((m) => (
                  <li
                    key={`${m.cli_binary}:${m.id}`}
                    className="flex items-center gap-2 px-3 py-2 rounded-md bg-bg-inset"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-body text-text-primary truncate">
                        {m.label}
                        {m.is_alias && (
                          <span className="ml-2 text-caption text-accent">alias</span>
                        )}
                        {m.default && (
                          <span className="ml-2 text-caption text-text-tertiary">default</span>
                        )}
                      </div>
                      <div className="text-mono text-text-tertiary truncate">
                        {m.id || "(no --model flag)"}
                      </div>
                    </div>
                    {!m.builtin && (
                      <IconButton
                        icon={<Trash />}
                        label={`Remove ${m.label}`}
                        size="sm"
                        onClick={() => remove.mutate(m)}
                      />
                    )}
                  </li>
                ))}
            </ul>
          </div>
        ))}

        <div className="space-y-2 border-t border-border-subtle pt-4">
          <div className="text-caption text-text-tertiary">Add a model</div>
          <div className="flex flex-wrap gap-2">
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Label (e.g. Opus 5)"
              className={fieldCls}
            />
            <input
              value={modelId}
              onChange={(e) => setModelId(e.target.value)}
              placeholder="model id (e.g. claude-opus-5 or alias 'opus')"
              className={fieldCls}
            />
            <select
              value={cliBinary}
              onChange={(e) => setCliBinary(e.target.value)}
              className={fieldCls}
            >
              <option value="claude">claude</option>
              <option value="codex">codex</option>
            </select>
            <label className="flex items-center gap-1.5 text-small text-text-secondary">
              <input
                type="checkbox"
                checked={isAlias}
                onChange={(e) => setIsAlias(e.target.checked)}
              />
              alias
            </label>
          </div>
          {err && <p className="text-small text-status-error">{err}</p>}
          <div className="flex justify-between gap-2 pt-1">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => refresh.mutate()}
                disabled={refresh.isPending}
                title="Restore the curated built-in models to their current set"
              >
                {refresh.isPending ? "Refreshing…" : "Refresh built-ins"}
              </Button>
              <span className="text-caption text-text-tertiary">
                {refreshedAt ? `Refreshed ${refreshedAt}` : "Never refreshed"}
              </span>
            </div>
            <Button
              size="sm"
              onClick={() => add.mutate()}
              disabled={add.isPending || !label.trim()}
            >
              {add.isPending ? "Adding…" : "Add model"}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

const fieldCls = cn(
  "flex-1 min-w-[140px] px-3 py-1.5 rounded-md text-body",
  "bg-bg-inset border border-border-default text-text-primary",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
);
